import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Building2, Save, Upload, Loader2, CheckCircle, AlertCircle,
  FileText, MapPin, Phone, Mail, Globe, Hash, CreditCard,
  Info, Target, Eye, Heart, Star, Sparkles
} from 'lucide-react';

function CompanyProfile() {
  const { API, user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('statutory');
  
  const [companyData, setCompanyData] = useState({
    // Basic Info
    companyName: 'Swift Strips India',
    tagline: 'Aluminum Recycling & Processing',
    logo: '',
    
    // Statutory Details
    gstNumber: '',
    panNumber: '',
    cinNumber: '',
    tanNumber: '',
    ieCode: '', // Import Export Code
    
    // Contact Details
    registeredAddress: '',
    factoryAddress: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    phone: '',
    alternatePhone: '',
    email: '',
    website: '',
    
    // Bank Details
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    
    // Landing Page Content
    about: '',
    ourJourney: '',
    ourVision: '',
    ourMission: '',
    coreValues: []
  });

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/company-profile`);
      if (response.data) {
        setCompanyData(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const handleCoreValueChange = (index, field, value) => {
    setCompanyData(prev => {
      const newValues = [...(prev.coreValues || [])];
      if (!newValues[index]) {
        newValues[index] = { title: '', description: '' };
      }
      newValues[index][field] = value;
      return { ...prev, coreValues: newValues };
    });
  };

  const addCoreValue = () => {
    setCompanyData(prev => ({
      ...prev,
      coreValues: [...(prev.coreValues || []), { title: '', description: '' }]
    }));
  };

  const removeCoreValue = (index) => {
    setCompanyData(prev => ({
      ...prev,
      coreValues: prev.coreValues.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/company-profile`, companyData);
      setMessage({ type: 'success', text: 'Company profile saved successfully!' });
    } catch (error) {
      console.error('Error saving company data:', error);
      setMessage({ type: 'error', text: 'Failed to save company profile' });
    } finally {
      setSaving(false);
    }
    setTimeout(() => setMessage(null), 5000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  const tabs = [
    { id: 'statutory', label: 'Statutory Details', icon: FileText },
    { id: 'contact', label: 'Contact & Address', icon: MapPin },
    { id: 'bank', label: 'Bank Details', icon: CreditCard },
    { id: 'landing', label: 'Landing Page Content', icon: Globe },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Building2 className="text-blue-600" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
            <p className="text-sm text-gray-500">Manage company details and landing page content</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 font-medium shadow-md"
          data-testid="save-company-btn"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Company Name & Logo Preview */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-white/20 rounded-xl flex items-center justify-center">
            {companyData.logo ? (
              <img src={companyData.logo} alt="Logo" className="w-16 h-16 object-contain" />
            ) : (
              <Building2 size={40} className="text-white/70" />
            )}
          </div>
          <div>
            <input
              type="text"
              value={companyData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className="text-2xl font-bold bg-transparent border-none outline-none placeholder-white/50 w-full"
              placeholder="Company Name"
            />
            <input
              type="text"
              value={companyData.tagline}
              onChange={(e) => handleInputChange('tagline', e.target.value)}
              className="text-blue-100 bg-transparent border-none outline-none placeholder-white/30 w-full"
              placeholder="Company Tagline"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 -mb-[2px] ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {/* Statutory Details Tab */}
        {activeTab === 'statutory' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="text-blue-500" size={20} />
              Statutory Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={companyData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value.toUpperCase())}
                    placeholder="22AAAAA0000A1Z5"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={companyData.panNumber}
                    onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                    placeholder="AAAAA0000A"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIN Number</label>
                <input
                  type="text"
                  value={companyData.cinNumber}
                  onChange={(e) => handleInputChange('cinNumber', e.target.value.toUpperCase())}
                  placeholder="Corporate Identity Number"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TAN Number</label>
                <input
                  type="text"
                  value={companyData.tanNumber}
                  onChange={(e) => handleInputChange('tanNumber', e.target.value.toUpperCase())}
                  placeholder="Tax Deduction Account Number"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IE Code (Import Export)</label>
                <input
                  type="text"
                  value={companyData.ieCode}
                  onChange={(e) => handleInputChange('ieCode', e.target.value.toUpperCase())}
                  placeholder="Import Export Code"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Contact & Address Tab */}
        {activeTab === 'contact' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="text-blue-500" size={20} />
              Contact & Address Details
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered Address</label>
                <textarea
                  value={companyData.registeredAddress}
                  onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                  placeholder="Enter registered office address"
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Factory/Plant Address</label>
                <textarea
                  value={companyData.factoryAddress}
                  onChange={(e) => handleInputChange('factoryAddress', e.target.value)}
                  placeholder="Enter factory/plant address"
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={companyData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={companyData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="State"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    value={companyData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    placeholder="000000"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={companyData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Country"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={companyData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+91 00000 00000"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={companyData.alternatePhone}
                      onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
                      placeholder="+91 00000 00000"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="email"
                      value={companyData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="info@company.com"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="url"
                      value={companyData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://www.company.com"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bank Details Tab */}
        {activeTab === 'bank' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="text-blue-500" size={20} />
              Bank Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={companyData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="Bank Name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
                <input
                  type="text"
                  value={companyData.branchName}
                  onChange={(e) => handleInputChange('branchName', e.target.value)}
                  placeholder="Branch Name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  value={companyData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                  placeholder="Account Number"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                <input
                  type="text"
                  value={companyData.ifscCode}
                  onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                  placeholder="IFSC Code"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Landing Page Content Tab */}
        {activeTab === 'landing' && (
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> This content will be displayed on the landing page for all non-admin users when they login.
              </p>
            </div>

            <div className="space-y-6">
              {/* About */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Info className="text-blue-500" size={18} />
                  About Us
                </h3>
                <textarea
                  value={companyData.about}
                  onChange={(e) => handleInputChange('about', e.target.value)}
                  placeholder="Write about your company..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>

              {/* Our Journey */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Sparkles className="text-purple-500" size={18} />
                  Our Journey
                </h3>
                <textarea
                  value={companyData.ourJourney}
                  onChange={(e) => handleInputChange('ourJourney', e.target.value)}
                  placeholder="Describe your company's journey..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>

              {/* Our Vision */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Eye className="text-green-500" size={18} />
                  Our Vision
                </h3>
                <textarea
                  value={companyData.ourVision}
                  onChange={(e) => handleInputChange('ourVision', e.target.value)}
                  placeholder="What is your company's vision?"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>

              {/* Our Mission */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Target className="text-red-500" size={18} />
                  Our Mission
                </h3>
                <textarea
                  value={companyData.ourMission}
                  onChange={(e) => handleInputChange('ourMission', e.target.value)}
                  placeholder="What is your company's mission?"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>

              {/* Core Values */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                    <Heart className="text-pink-500" size={18} />
                    Our Core Values
                  </h3>
                  <button
                    onClick={addCoreValue}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 text-sm font-medium"
                  >
                    <Star size={14} />
                    Add Value
                  </button>
                </div>
                <div className="space-y-3">
                  {(companyData.coreValues || []).map((value, index) => (
                    <div key={index} className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={value.title || ''}
                          onChange={(e) => handleCoreValueChange(index, 'title', e.target.value)}
                          placeholder="Value Title (e.g., Integrity)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none text-sm font-medium"
                        />
                        <textarea
                          value={value.description || ''}
                          onChange={(e) => handleCoreValueChange(index, 'description', e.target.value)}
                          placeholder="Description of this core value..."
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removeCoreValue(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <AlertCircle size={16} />
                      </button>
                    </div>
                  ))}
                  {(!companyData.coreValues || companyData.coreValues.length === 0) && (
                    <p className="text-gray-500 text-sm text-center py-4 bg-gray-50 rounded-lg">
                      No core values added yet. Click "Add Value" to add your first core value.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyProfile;
